import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faMagnifyingGlass,
  faUpload,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { studentPhoto } from "./modal/preview-foto";

const CARD_WIDTH = 1011;
const CARD_HEIGHT = 638;
const BACKGROUND_STORAGE_KEY = "sims-kartu-pelajar-background";

const safeValue = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const toTitleCase = (value = "") =>
  safeValue(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getFirstFilled = (...values) => {
  for (const value of values) {
    if (safeValue(value)) return value;
  }
  return "";
};

const getNested = (object, path) =>
  path.split(".").reduce((result, key) => (result ? result[key] : undefined), object);

const getStudentName = (student) =>
  toTitleCase(
    getFirstFilled(
      student?.nama_lengkap,
      student?.nama_siswa,
      student?.name_student,
      student?.name,
      getNested(student, "biodata.nama_lengkap")
    )
  ) || "-";

const formatDate = (value) => {
  const raw = safeValue(value);
  if (!raw) return "";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getBirthText = (student) => {
  const birthPlace = toTitleCase(
    getFirstFilled(
      student?.tempat_lahir,
      student?.birth_place,
      student?.place_of_birth,
      getNested(student, "biodata.tempat_lahir"),
      getNested(student, "biodata.birth_place")
    )
  );

  const birthDate = formatDate(
    getFirstFilled(
      student?.tanggal_lahir_siswa,
      student?.tgl_lahir,
      student?.date_birth,
      student?.birth_date,
      getNested(student, "biodata.tanggal_lahir_siswa"),
      getNested(student, "biodata.tgl_lahir"),
      getNested(student, "biodata.birth_date")
    )
  );

  if (birthPlace && birthDate) return `${birthPlace}, ${birthDate}`;
  return birthPlace || birthDate || "-";
};

const getAddress = (student) =>
  toTitleCase(
    getFirstFilled(
      student?.alamat,
      student?.address,
      student?.alamat_lengkap,
      getNested(student, "biodata.alamat"),
      getNested(student, "biodata.address"),
      getNested(student, "biodata.alamat_lengkap")
    )
  ) || "-";

const getNis = (student) =>
  getFirstFilled(
    student?.nis,
    student?.no_induk,
    student?.no_induk_siswa,
    student?.student_number,
    getNested(student, "biodata.nis"),
    getNested(student, "biodata.no_induk")
  ) || "-";

const getNisn = (student) => safeValue(student?.nisn) || "-";

const sanitizeFileName = (value) =>
  safeValue(value)
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim() || "siswa";

const dataUrlToBlob = (dataUrl) => {
  const [meta, base64] = dataUrl.split(",");
  const mime = meta.match(/data:(.*?);base64/)?.[1] || "image/jpeg";
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
};

const waitForImages = async (root) => {
  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    })
  );
};

const InfoRow = ({ label, value }) => (
  <div className="sims-kp-row">
    <div className="sims-kp-label">{label}</div>
    <div className="sims-kp-colon">:</div>
    <div className="sims-kp-value">{value || "-"}</div>
  </div>
);

const StudentCardDesign = ({ student, background, className = "" }) => {
  const photo = studentPhoto(student);

  return (
    <div
      className={`sims-kp-stage ${className}`.trim()}
      style={{
        width: `${CARD_WIDTH}px`,
        height: `${CARD_HEIGHT}px`,
        ...(background ? { backgroundImage: `url(${background})` } : {}),
      }}
    >
      {!background && (
        <div className="sims-kp-watermark" aria-hidden="true">
          {Array.from({ length: 24 }).map((_, index) => (
            <span key={index}>background belum diupload</span>
          ))}
        </div>
      )}

      <div className="sims-kp-center-content">
        <div className="sims-kp-photo-wrap">
          {photo ? (
            <img src={photo} alt={getStudentName(student)} className="sims-kp-photo" />
          ) : (
            <div className="sims-kp-photo-empty">Foto 3x4</div>
          )}
        </div>

        <div className="sims-kp-data">
          <InfoRow label="Nama" value={getStudentName(student)} />
          <InfoRow label="NISN" value={getNisn(student)} />
          <InfoRow label="NIS" value={getNis(student)} />
          <InfoRow label="TTL" value={getBirthText(student)} />
          <InfoRow label="Alamat" value={getAddress(student)} />
        </div>
      </div>
    </div>
  );
};

const FixedStudentCard = ({ student, background }) => {
  const shellRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;

    const updateScale = () => {
      const width = shell.clientWidth || CARD_WIDTH;
      setScale(width / CARD_WIDTH);
    };

    updateScale();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateScale);
      observer.observe(shell);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <div
      ref={shellRef}
      className="sims-kp-stage-shell"
      style={{ aspectRatio: `${CARD_WIDTH} / ${CARD_HEIGHT}` }}
    >
      <div
        className="sims-kp-scaled-stage"
        style={{ transform: `scale(${scale})` }}
      >
        <StudentCardDesign student={student} background={background} />
      </div>
    </div>
  );
};

export default function KartuPelajarTab({
  students = [],
  backgroundImage = "",
  academicYear = "",
  levelName = "",
  className = "",
}) {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [uploadedBackground, setUploadedBackground] = useState("");
  const [previewStudent, setPreviewStudent] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const backgroundInputRef = useRef(null);
  const exportContainerRef = useRef(null);

  useEffect(() => {
    try {
      const savedBackground = window.localStorage.getItem(BACKGROUND_STORAGE_KEY);
      if (savedBackground) setUploadedBackground(savedBackground);
    } catch (error) {
      console.warn("Background lokal tidak dapat dibaca:", error);
    }
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") setPreviewStudent(null);
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const activeBackground = uploadedBackground || backgroundImage;

  const handleBackgroundUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      if (!result) return;

      setUploadedBackground(result);

      try {
        window.localStorage.setItem(BACKGROUND_STORAGE_KEY, result);
      } catch (error) {
        console.warn("Background terlalu besar untuk disimpan di localStorage:", error);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleRemoveBackground = () => {
    setUploadedBackground("");
    try {
      window.localStorage.removeItem(BACKGROUND_STORAGE_KEY);
    } catch (error) {
      console.warn("Background lokal tidak dapat dihapus:", error);
    }
  };

  const filteredStudents = useMemo(() => {
    const keyword = safeValue(searchKeyword).toLowerCase();
    if (!keyword) return students;

    return students.filter((student) => {
      const name = getStudentName(student).toLowerCase();
      const nisn = getNisn(student).toLowerCase();
      const nis = getNis(student).toLowerCase();
      return name.includes(keyword) || nisn.includes(keyword) || nis.includes(keyword);
    });
  }, [students, searchKeyword]);

  const handleDownloadZip = async () => {
    if (!students.length || isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress({ current: 0, total: students.length });

    try {
      const exportRoot = exportContainerRef.current;
      if (!exportRoot) throw new Error("Area export kartu tidak ditemukan.");

      await waitForImages(exportRoot);

      const zip = new JSZip();
      const cards = Array.from(exportRoot.querySelectorAll("[data-export-card]"));

      for (let index = 0; index < cards.length; index += 1) {
        const card = cards[index];
        const student = students[index];

        const canvas = await html2canvas(card, {
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
          scale: 1,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
        });

        const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.95);
        const jpegBlob = dataUrlToBlob(jpegDataUrl);
        const studentName = sanitizeFileName(getStudentName(student));
        const nisn = sanitizeFileName(getNisn(student));
        const fileName = `${String(index + 1).padStart(2, "0")} - ${studentName} - ${nisn}.jpg`;

        zip.file(fileName, jpegBlob);
        setDownloadProgress({ current: index + 1, total: students.length });
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const anchor = document.createElement("a");
      anchor.href = url;
      const safeAcademicYear = sanitizeFileName(academicYear || "Tahun Ajaran")
        .replace(/\//g, "-");
      const safeLevelName = sanitizeFileName(levelName || "Tingkatan")
        .replace(/^kelas\s+/i, "")
        .trim();
      const safeClassName = sanitizeFileName(className || "Kelas")
        .replace(/^kelas\s+/i, "")
        .trim();

      anchor.download = `(${safeAcademicYear}) Kartu Pelajar Kelas ${safeLevelName} ${safeClassName}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Gagal download kartu pelajar:", error);
      window.alert("Kartu pelajar gagal dibuat. Silakan coba lagi.");
    } finally {
      setIsDownloading(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="sims-kp-tab">
      <style>{`
        .sims-kp-toolbar {
          display:flex;
          flex-direction:column;
          gap:12px;
          padding:16px 8px 8px;
        }
        .sims-kp-search-row {
          width:100%;
          min-width:0;
        }
        .sims-kp-action-row {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:14px;
          flex-wrap:wrap;
          width:100%;
        }
        .sims-kp-toolbar-left,
        .sims-kp-toolbar-right {
          display:flex;
          align-items:center;
          gap:10px;
          flex-wrap:wrap;
          min-width:0;
        }
        .sims-kp-toolbar-left { flex:1; }
        .sims-kp-upload-btn,
        .sims-kp-remove-bg-btn,
        .sims-kp-download-btn {
          height:42px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          border-radius:10px;
          padding:0 14px;
          font-size:13px;
         
          cursor:pointer;
          transition:.2s ease;
          white-space:nowrap;
        }
        .sims-kp-upload-btn {
          border:1px solid #3f5bea;
          background:#3f5bea;
          color:#fff;
        }
        .sims-kp-upload-btn:hover { background:#334ccd; border-color:#334ccd; }
        .sims-kp-remove-bg-btn {
          border:1px solid #d8e0ed;
          background:#fff;
          color:#56657f;
        }
        .sims-kp-remove-bg-btn:hover { background:#f6f8fb; }
        .sims-kp-download-btn {
          border:1px solid #f6c514;
          background:#f6c514;
          color:#000;
        }
        .sims-kp-download-btn:hover:not(:disabled) { background:#117c50; border-color:#117c50; }
        .sims-kp-download-btn:disabled { opacity:.65; cursor:not-allowed; }

        .sims-kp-search { position:relative; width:100%; }
        .sims-kp-search svg {
          position:absolute;
          left:14px;
          top:50%;
          transform:translateY(-50%);
          color:#7f8aa3;
          font-size:13px;
          pointer-events:none;
        }
        .sims-kp-search input {
          width:100%;
          height:42px;
          border:1px solid #d9e2f0;
          border-radius:10px;
          padding:0 14px 0 39px;
          outline:none;
          background:#fff;
          color:#24344d;
          font-size:13px;
          transition:.2s ease;
        }
        .sims-kp-search input:focus {
          border-color:#6179e9;
          box-shadow:0 0 0 3px rgba(63,91,234,.08);
        }
        .sims-kp-summary { margin:0; color:#74819a; font-size:12px; }

        .sims-kp-grid {
          display:grid;
          grid-template-columns:repeat(3, minmax(0, 1fr));
          gap:18px;
          padding:16px 8px 28px;
          align-items:start;
        }
        .sims-kp-item { min-width:0; width:100%; cursor:pointer; }
        .sims-kp-item .sims-kp-stage-shell { transition:.2s ease; }
        .sims-kp-item:hover .sims-kp-stage-shell {
          transform:translateY(-2px);
          box-shadow:0 12px 28px rgba(34,51,84,.14);
          border-color:#bfcdf0;
        }

        .sims-kp-stage-shell {
          position:relative;
          width:100%;
          overflow:hidden;
          border:1px solid #dbe4f0;
          border-radius:16px;
          background:#fff;
          box-shadow:0 8px 22px rgba(34,51,84,.08);
        }
        .sims-kp-scaled-stage {
          position:absolute;
          left:0;
          top:0;
          width:${CARD_WIDTH}px;
          height:${CARD_HEIGHT}px;
          transform-origin:top left;
        }
        .sims-kp-stage {
          position:relative;
          overflow:hidden;
          background-color:#ffffff;
          background-position:center;
          background-repeat:no-repeat;
          background-size:cover;
          display:flex;
          align-items:center;
          justify-content:center;
          box-sizing:border-box;
        }
        .sims-kp-watermark {
          position:absolute;
          inset:-90px;
          z-index:0;
          display:grid;
          grid-template-columns:repeat(3, 1fr);
          align-content:center;
          justify-items:center;
          gap:58px 24px;
          transform:rotate(-28deg);
          pointer-events:none;
          overflow:hidden;
        }
        .sims-kp-watermark span {
          color:rgba(123, 140, 180, 0.23);
          font-size:30px;
          font-weight:800;
          white-space:nowrap;
          text-transform:lowercase;
          letter-spacing:.3px;
          user-select:none;
        }
        .sims-kp-center-content {
          position:relative;
          z-index:1;
          width:760px;
          display:grid;
          grid-template-columns:220px minmax(0, 1fr);
          gap:12px;
          align-items:start;
          margin:auto;
          padding-top:50px;
        }
        .sims-kp-photo-wrap {
          width:190px;
          height:263px;
          overflow:hidden;
          border-radius:18px;
          border:2px solid #d7dfec;
          background:#eef3f8;
          box-sizing:border-box;
        }
        .sims-kp-photo {
          width:100%;
          height:100%;
          object-fit:cover;
          object-position:center top;
          display:block;
        }
        .sims-kp-photo-empty {
          width:100%;
          height:100%;
          display:flex;
          align-items:center;
          justify-content:center;
          text-align:center;
          padding:20px;
          color:#8a96ab;
          font-size:28px;
          font-weight:700;
          line-height:1.35;
          background:#eef3f8;
          box-sizing:border-box;
        }
        .sims-kp-data {
          min-width:0;
          display:flex;
          flex-direction:column;
          gap:5px;
        }
        .sims-kp-row {
          display:grid;
          grid-template-columns:120px 22px minmax(0, 1fr);
          gap:8px;
          align-items:start;
          font-size:22px;
          line-height:1.35;
          color:#2a3448;
        }
        .sims-kp-label,
        .sims-kp-colon { font-weight:700; color:#24344d; }
        .sims-kp-value {
          min-width:0;
          overflow-wrap:break-word;
          word-break:normal;
          color:#24344d;
        }
        .sims-kp-empty {
          margin:16px 8px 24px;
          border:1px dashed #cfdaea;
          border-radius:14px;
          background:#fbfdff;
          padding:28px 18px;
          text-align:center;
          color:#7c8aa3;
          font-size:13px;
          font-weight:600;
        }

        .sims-kp-modal-backdrop {
          position:fixed;
          inset:0;
          z-index:9999;
          background:rgba(16,24,40,.64);
          display:flex;
          align-items:center;
          justify-content:center;
          padding:24px;
        }
        .sims-kp-modal {
          width:min(1100px, 96vw);
          max-height:94vh;
          overflow:auto;
          background:#fff;
          border-radius:18px;
          box-shadow:0 24px 70px rgba(16,24,40,.28);
        }
        .sims-kp-modal-header {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:16px;
          padding:16px 18px;
          border-bottom:1px solid #e7edf5;
        }
        .sims-kp-modal-title { margin:0; font-size:16px; color:#22314b; font-weight:800; }
        .sims-kp-modal-close {
          width:36px;
          height:36px;
          border:0;
          border-radius:9px;
          background:#f1f4f8;
          color:#4c5b73;
          cursor:pointer;
        }
        .sims-kp-modal-body { padding:20px; }
        .sims-kp-modal-card {
          width:min(100%, ${CARD_WIDTH}px);
          margin:0 auto;
        }

        .sims-kp-export-root {
          position:fixed;
          left:-20000px;
          top:0;
          width:${CARD_WIDTH}px;
          pointer-events:none;
          opacity:1;
          z-index:-1;
        }
        .sims-kp-export-card {
          width:${CARD_WIDTH}px;
          height:${CARD_HEIGHT}px;
        }

        @media (max-width: 1100px) {
          .sims-kp-grid { grid-template-columns:repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 720px) {
          .sims-kp-grid { grid-template-columns:1fr; gap:14px; }
          .sims-kp-action-row { align-items:stretch; }
          .sims-kp-toolbar-left,
          .sims-kp-toolbar-right { width:100%; }
          .sims-kp-toolbar-right { justify-content:space-between; }
          .sims-kp-modal-backdrop { padding:10px; }
          .sims-kp-modal-body { padding:12px; }
        }
      `}</style>

      <div className="sims-kp-toolbar">
        <div className="sims-kp-search-row">
          <div className="sims-kp-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="text"
              placeholder="Cari nama, NISN, atau NIS..."
              value={searchKeyword}
              onChange={(event) => setSearchKeyword(event.target.value)}
            />
          </div>
        </div>

        <div className="sims-kp-action-row">
          <div className="sims-kp-toolbar-left">
            <input
              ref={backgroundInputRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundUpload}
              style={{ display: "none" }}
            />

            <button
              type="button"
              className="sims-kp-upload-btn"
              onClick={() => backgroundInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={faUpload} />
              <span>{activeBackground ? "Ganti Background" : "Upload Background"}</span>
            </button>

            {uploadedBackground && (
              <button
                type="button"
                className="sims-kp-remove-bg-btn"
                onClick={handleRemoveBackground}
              >
                <FontAwesomeIcon icon={faXmark} />
                <span>Hapus Background</span>
              </button>
            )}
          </div>

          <div className="sims-kp-toolbar-right">
            <p className="sims-kp-summary">
              Menampilkan <b>{filteredStudents.length}</b> dari <b>{students.length}</b> siswa
            </p>

            <button
              type="button"
              className="sims-kp-download-btn"
              onClick={handleDownloadZip}
              disabled={!students.length || isDownloading}
            >
              <FontAwesomeIcon icon={faDownload} />
              <span>
                {isDownloading
                  ? `Membuat ZIP ${downloadProgress.current}/${downloadProgress.total}`
                  : "Download Semua Kartu (.ZIP)"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {filteredStudents.length === 0 ? (
        <div className="sims-kp-empty">Data kartu pelajar belum tersedia.</div>
      ) : (
        <div className="sims-kp-grid">
          {filteredStudents.map((student, index) => (
            <div
              role="button"
              tabIndex={0}
              className="sims-kp-item"
              key={student?.ysb_student_id || student?.id || `kartu-${index}`}
              onClick={() => setPreviewStudent(student)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setPreviewStudent(student);
                }
              }}
              title="Klik untuk preview kartu pelajar"
            >
              <FixedStudentCard student={student} background={activeBackground} />
            </div>
          ))}
        </div>
      )}

      {previewStudent && (
        <div
          className="sims-kp-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreviewStudent(null);
          }}
        >
          <div className="sims-kp-modal" role="dialog" aria-modal="true">
            <div className="sims-kp-modal-header">
              <h3 className="sims-kp-modal-title">
                Preview Kartu Pelajar - {getStudentName(previewStudent)}
              </h3>
              <button
                type="button"
                className="sims-kp-modal-close"
                onClick={() => setPreviewStudent(null)}
                aria-label="Tutup preview"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="sims-kp-modal-body">
              <div className="sims-kp-modal-card">
                <FixedStudentCard student={previewStudent} background={activeBackground} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={exportContainerRef} className="sims-kp-export-root" aria-hidden="true">
        {students.map((student, index) => (
          <div
            className="sims-kp-export-card"
            data-export-card
            key={`export-${student?.ysb_student_id || student?.id || index}`}
          >
            <StudentCardDesign student={student} background={activeBackground} />
          </div>
        ))}
      </div>
    </div>
  );
}
